import React from 'react';
import PropTypes from 'prop-types';

/**
 * HighlightedText 컴포넌트
 * 텍스트에서 지정된 부분을 강조 표시하는 컴포넌트
 * 
 * Props:
 * @param {React.ReactNode} children - 텍스트 내용 (JSX 형태) [Required]
 * @param {number} normalWeight - 일반 텍스트 fontWeight [Optional, 기본값: 300]
 * @param {number} boldWeight - 강조 텍스트 fontWeight [Optional, 기본값: 800]
 * @param {string} letterSpacing - 글자 간격 [Optional, 기본값: "0.01em"]
 * @param {object} style - 추가 스타일 [Optional]
 * @param {string} className - CSS 클래스명 [Optional]
 * 
 * Example usage:
 * <HighlightedText letterSpacing="0.01em">
 *   오늘도 Threads에 <HighlightedText.Bold>어떤글을</HighlightedText.Bold> 올릴지
 *   <br />
 *   <HighlightedText.Bold>고민</HighlightedText.Bold>하고 게신가요?
 * </HighlightedText>
 */
function HighlightedText({ 
  children, 
  normalWeight = 300, 
  boldWeight = 800,
  letterSpacing = "0.01em",
  style = {},
  className = '',
  ...props 
}) {
  return (
    <span 
      style={{ 
        fontWeight: normalWeight,
        letterSpacing: letterSpacing,
        ...style 
      }}
      className={className}
      {...props}
    >
      {children}
    </span>
  );
}

/**
 * Bold 서브컴포넌트
 * 강조할 텍스트를 감싸는 컴포넌트
 */
HighlightedText.Bold = function Bold({ 
  children, 
  weight,
  style = {},
  ...props 
}) {
  // 부모 HighlightedText의 boldWeight를 사용하거나 기본값 800 사용
  const fontWeight = weight || 800;
  
  return (
    <span 
      style={{ 
        fontWeight: fontWeight,
        ...style 
      }}
      {...props}
    >
      {children}
    </span>
  );
};

/**
 * Break 서브컴포넌트
 * 줄바꿈을 위한 컴포넌트
 */
HighlightedText.Break = function Break() {
  return <br />;
};

// PropTypes 정의
HighlightedText.propTypes = {
  children: PropTypes.node.isRequired,
  normalWeight: PropTypes.number,
  boldWeight: PropTypes.number,
  letterSpacing: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string
};

HighlightedText.Bold.propTypes = {
  children: PropTypes.node.isRequired,
  weight: PropTypes.number,
  style: PropTypes.object
};

export default HighlightedText; 